import type { Creator } from "@/shared/types/campaign/creator";

export const EXPORT_NO_DATA = "N/A";

export const EXPORT_PLATFORMS = ["tiktok", "instagram", "facebook", "youtube"] as const;

export const EXPORT_AGE_RANGES = ["13-17", "18-24", "25-34", "35-44", "45-64", "65+"] as const;

// Some accounts store the oldest bucket as "65-"; it means 65+.
const AGE_RANGE_ALIASES: Record<string, string> = { "65-": "65+" };

const hasValue = (value: unknown): value is number | string =>
	value !== null && value !== undefined && value !== "" && Number(value) !== 0 && !Number.isNaN(Number(value));

const formatCount = (value: unknown): number | string => (hasValue(value) ? Number(value) : EXPORT_NO_DATA);

const isNumber = (value: unknown): boolean =>
	value !== null && value !== undefined && value !== "" && !Number.isNaN(Number(value));

// A real 0% is shown as 0%; only missing values are N/A.
const formatPercent = (value: unknown, decimals = 1): string =>
	isNumber(value) ? `${Number(value).toFixed(decimals)}%` : EXPORT_NO_DATA;

const formatText = (value: unknown): string => {
	const text = Array.isArray(value) ? value.filter(Boolean).join(", ") : value == null ? "" : String(value);
	return text.trim() ? text : EXPORT_NO_DATA;
};

const capitalize = (platform: string) => platform.charAt(0).toUpperCase() + platform.slice(1);

/** Sums the audience share per age range (an account can list a range more than once, e.g. split by gender). */
export const sumAudienceAgeByRange = (distribution: unknown): Record<string, number> => {
	const totals: Record<string, number> = {};
	if (!Array.isArray(distribution)) return totals;
	distribution.forEach((bucket) => {
		const rawRange = String(bucket?.ageRange ?? "").trim();
		const range = AGE_RANGE_ALIASES[rawRange] ?? rawRange;
		const percentage = Number(bucket?.percentage);
		if (!range || Number.isNaN(percentage)) return;
		totals[range] = (totals[range] ?? 0) + percentage;
	});
	return totals;
};

/**
 * One export row per creator. Every platform gets the same columns (N/A when the creator has no account or the
 * field is hidden), so the CSV/XLSX header is identical whatever the first creator has.
 */
export const flattenCreatorForExport = (creator: Creator): Record<string, string | number> => {
	const row: Record<string, string | number> = {
		"Creator Name": formatText(creator.name),
		Category: formatText(creator.category ?? creator.categories),
		Tier: formatText(creator.creatorTier ?? creator.tier),
		Location: formatText(creator.location),
	};

	EXPORT_PLATFORMS.forEach((platform) => {
		const account = creator[platform] && typeof creator[platform] === "object" ? creator[platform] : {};
		const name = capitalize(platform);
		const ages = sumAudienceAgeByRange(account.audienceAgeDistribution);

		// The API sends a missing gender split as 0, so a 0% side is only real when the other side is above 0.
		const hasGenderSplit = hasValue(account.femalePercentage) || hasValue(account.malePercentage);

		row[`${name} Followers`] = formatCount(account.followers);
		// avgEngagementRate is a 0–1 ratio. 0 means it was never fetched (the API sends missing as 0), not 0%.
		row[`${name} Avg Engagement Rate`] = hasValue(account.avgEngagementRate)
			? formatPercent(Number(account.avgEngagementRate) * 100, 2)
			: EXPORT_NO_DATA;
		row[`${name} Avg Views`] = formatCount(account.avgViews);
		row[`${name} Local Audience %`] = formatPercent(account.localAudiencePercentage);
		row[`${name} Female %`] = hasGenderSplit ? formatPercent(account.femalePercentage ?? 0) : EXPORT_NO_DATA;
		row[`${name} Male %`] = hasGenderSplit ? formatPercent(account.malePercentage ?? 0) : EXPORT_NO_DATA;
		EXPORT_AGE_RANGES.forEach((range) => {
			row[`${name} Age ${range} %`] = range in ages ? formatPercent(ages[range]) : EXPORT_NO_DATA;
		});
	});

	return row;
};

export const flattenCreatorsForExport = (creators: Creator[]) => creators.map(flattenCreatorForExport);
